import React, { useState } from 'react';
import { Navigate, Routes, Route, Outlet } from '@common/components';
import { PrivateRoute } from '@routes/private-route';
import { SupplierAdminVerticalNavMenu } from 'works/pages/supplier/admin-settings/supplier-admin-vertical-nav-menu/supplier-admin-vertical-nav-menu';
import {
  AdminMembersPage,
  MembersAddEditSteps,
  MembersAddMultipleSteps,
  FourZeroFourPage,
} from '@identity/pages';
import {
  AdminSupplierDetailsPage,
  OrganizationInvitation,
} from '@institutions/pages/supplier';
import { useCurrentMembership } from '@identity/hooks';

const permissionKeys = ['canShowAdminMembers'] as const;

type PermissionKey = (typeof permissionKeys)[number];

type PermissionsSet = Record<PermissionKey, boolean>;

export function SupplierAdminSettingsRoutes() {
  const [showMenu, setShowMenu] = useState(true);
  const currentMembership = useCurrentMembership();

  const permissions = permissionKeys.reduce((acc, key) => {
    acc[key] = currentMembership[key] ?? false;
    return acc;
  }, {} as PermissionsSet);

  return (
    <Routes>
      <Route
        element={
          <SupplierAdminVerticalNavMenu
            permissions={permissions}
            hideMenu={!showMenu}
          >
            {/* All the nested routes defined down below will be rendered by this
             /* Outlet component, as child of the 'works/supplier/admin' route */}
            <Outlet />
          </SupplierAdminVerticalNavMenu>
        }
      >
        {/* DEFAULT LANDING PAGE */}

        <Route
          path="/"
          element={<Navigate to="/works/supplier/admin/details" replace />}
        />

        {/* DETAILS */}

        <Route
          path="/details"
          element={
            <PrivateRoute
              menuItem="admin"
              page={AdminSupplierDetailsPage}
              onRender={() => setShowMenu(true)}
            />
          }
        />

        {/* USERS / MEMBERS */}

        <Route
          path="/users"
          element={
            <PrivateRoute
              menuItem="admin"
              page={AdminMembersPage}
              secondaryMenuItem="members-page"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminMembers}
            />
          }
        />
        <Route
          path="/users/add"
          element={
            <PrivateRoute
              menuItem="admin"
              page={MembersAddEditSteps}
              secondaryMenuItem="members-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminMembers}
            />
          }
        />
        <Route
          path="/users/edit/:email"
          element={
            <PrivateRoute
              menuItem="admin"
              page={MembersAddEditSteps}
              secondaryMenuItem="members-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminMembers}
            />
          }
        />
        <Route
          path="/users/upload"
          element={
            <PrivateRoute
              menuItem="admin"
              page={MembersAddMultipleSteps}
              secondaryMenuItem="members-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminMembers}
            />
          }
        />

        {/* ORGANIZATION INVITATIONS */}

        <Route
          path="/organization-invitations/invitation/:invitationToken"
          element={
            <PrivateRoute
              menuItem="admin"
              page={OrganizationInvitation}
              onRender={() => setShowMenu(false)}
            />
          }
        />

        {/* 404 PAGE */}

        <Route path="*" element={<FourZeroFourPage />} />
      </Route>
    </Routes>
  );
}
